import React from "react";
import { useParams, Link } from "react-router-dom";
import PageLayout from "@/components/PageLayout";
import { Palette, BarChart3, Video, Share2, Megaphone, Brush, FileText, TrendingUp, CheckCircle, ExternalLink } from "lucide-react";

// Image imports for the Our Work section
import digitalMarketingWork1 from "@/assets/digital marketing service 1.jpeg";
import digitalMarketingWork2 from "@/assets/digital marketing service 2.jpeg";
import digitalMarketingWork3 from "@/assets/digital marketing service 3.jpeg";

const servicesData: Record<string, {
  icon: React.ElementType;
  title: string;
  subtitle: string;
  description: string[];
  features: string[];
  stats: { label: string; value: string }[];
}> = {
  "graphic-designing": {
    icon: Palette,
    title: "Graphic Designing",
    subtitle: "Professional designs that elevate your brand identity",
    description: [
      "At Graphic Lines, we create stunning visuals that connect with your audience and elevate your brand. From logos to complete brand identity kits, our designs are crafted to leave a lasting impression.",
      "Our graphic design services cover everything from social media posts and ad creatives to flyers, posters, banners, infographics, and packaging designs — all tailored to your brand's unique identity.",
    ],
    features: [
      "Logo Design – Memorable and professional brand logos",
      "Social Media Posts – Engaging graphics for Instagram, Facebook, LinkedIn",
      "Ad Creatives – Eye-catching visuals for online ad campaigns",
      "Flyers & Posters – Promotional material for events and campaigns",
      "Brand Identity Kits – Complete branding packages",
      "Banner Designs – Web banners, email headers, and display ads",
      "Infographics – Visual content to explain ideas and stats clearly",
      "Packaging Designs – Attractive product packaging visuals",
    ],
    stats: [
      { label: "Satisfied Clients", value: "30+" },
      { label: "Completed Orders", value: "90+" },
      { label: "Brands Joined", value: "37+" },
    ],
  },
  "digital-marketing": {
    icon: BarChart3,
    title: "Digital Marketing",
    subtitle: "Data-driven strategies for measurable growth",
    description: [
      "Our digital marketing services combine creative content with strategic planning to help your brand reach the right audience and achieve measurable results.",
      "From content strategy and paid ads to influencer marketing and analytics, we deliver comprehensive digital marketing solutions for businesses of all sizes.",
    ],
    features: [
      "Content Strategy – Tailored plans for posts, campaigns, and engagement",
      "Social Media Management – Daily posting, monitoring, and community engagement",
      "Paid Ads Campaigns – Facebook, Instagram, and LinkedIn ads with measurable ROI",
      "Influencer Marketing – Collaborations with niche influencers",
      "Analytics & Reporting – Performance tracking with actionable insights",
      "Brand Awareness Campaigns – Strategies to increase visibility",
      "Engagement & Growth – Strategies to grow followers and interactions",
      "Hashtag & Trend Optimization – Using trending content for maximum reach",
    ],
    stats: [
      { label: "Brands Joined", value: "15+" },
      { label: "Satisfied Clients", value: "12+" },
    ],
  },
  "video-editing": {
    icon: Video,
    title: "Video Editing",
    subtitle: "High-quality video content that captivates audiences",
    description: [
      "Our video editing team creates professional, engaging video content for all platforms. From promotional ads to corporate presentations, we bring your vision to life.",
      "We specialize in social media clips, YouTube editing, motion graphics, event coverage, and product demo videos that drive engagement and conversions.",
    ],
    features: [
      "Promotional Videos – Short ads for products, services, or events",
      "Social Media Clips – Engaging videos for Instagram, Facebook, TikTok",
      "Corporate Videos – Professional brand storytelling",
      "YouTube Editing – Editing, intros, outros, and thumbnails",
      "Animation & Motion Graphics – Dynamic animated elements",
      "Event Coverage Editing – Highlights and cinematic edits",
      "Ad Video Creatives – High-converting video content for paid ads",
      "Product Demo Videos – Attractive product presentations",
    ],
    stats: [
      { label: "Projects Completed", value: "240+" },
      { label: "Satisfied Clients", value: "60+" },
      { label: "Brands Joined", value: "67+" },
      { label: "Active Clients", value: "5+" },
    ],
  },
  "social-media": {
    icon: Share2,
    title: "Social Media Management",
    subtitle: "Build engagement and brand presence across platforms",
    description: [
      "We manage your social media presence from strategy to content, building engagement and brand presence across all platforms.",
      "Our team handles daily posting, community management, audience growth, and performance analytics to keep your brand active and growing.",
    ],
    features: [
      "Content strategy & calendar planning",
      "Daily posting & community management",
      "Audience growth & engagement",
      "Analytics & performance reporting",
      "Influencer collaboration",
      "Brand reputation management",
    ],
    stats: [
      { label: "Satisfied Clients", value: "73+" },
      { label: "Brands Managed", value: "80+" },
    ],
  },
  "ads-management": {
    icon: Megaphone,
    title: "Ads Management",
    subtitle: "Optimized paid campaigns with strong ROI",
    description: [
      "Our ad specialists run optimized paid campaigns that scale sales while maintaining strong ROI across Facebook, Instagram, and Google.",
      "From campaign setup to ongoing optimization, we manage your ad spend efficiently to deliver maximum returns.",
    ],
    features: [
      "Facebook & Instagram ad campaigns",
      "Google Ads management",
      "Audience targeting & retargeting",
      "A/B testing & optimization",
      "Landing page creation",
      "ROI tracking & reporting",
    ],
    stats: [
      { label: "Campaigns Run", value: "200+" },
      { label: "Satisfied Clients", value: "50+" },
    ],
  },
  "branding": {
    icon: Brush,
    title: "Branding",
    subtitle: "Complete brand identity that defines your business",
    description: [
      "We create complete brand identities that tell your story and connect with your audience. From logos to style guides, we build brands that stand out.",
      "Our branding services include visual identity, brand strategy, and creative direction to ensure consistency across all touchpoints.",
    ],
    features: [
      "Logo & visual identity design",
      "Color palette & typography selection",
      "Brand style guides",
      "Brand voice & messaging",
      "Business card & stationery design",
      "Brand strategy & positioning",
    ],
    stats: [
      { label: "Brands Created", value: "80+" },
      { label: "Satisfied Clients", value: "73+" },
    ],
  },
  "content-creation": {
    icon: FileText,
    title: "Content Creation",
    subtitle: "Engaging content that drives traffic and conversions",
    description: [
      "We create compelling content for websites, social media, blogs, and marketing campaigns that engages your audience and drives results.",
      "Our content team combines creativity with strategy to produce content that builds authority and converts visitors into customers.",
    ],
    features: [
      "Blog writing & articles",
      "Social media content",
      "Website copywriting",
      "Email marketing content",
      "Video scripts",
      "Marketing collateral",
    ],
    stats: [
      { label: "Content Pieces", value: "500+" },
      { label: "Satisfied Clients", value: "40+" },
    ],
  },
  "marketing-strategy": {
    icon: TrendingUp,
    title: "Marketing Strategy",
    subtitle: "Data-driven strategies for sustainable growth",
    description: [
      "We develop comprehensive marketing strategies that combine branding, ads, social media, and content to reach your target audience effectively.",
      "Our strategic approach ensures every marketing dollar is spent wisely, delivering measurable results and sustainable business growth.",
    ],
    features: [
      "Market research & analysis",
      "Competitive analysis",
      "Target audience identification",
      "Campaign planning & execution",
      "Performance measurement",
      "Growth optimization",
    ],
    stats: [
      { label: "Strategies Delivered", value: "100+" },
      { label: "Satisfied Clients", value: "60+" },
    ],
  },
};

const ServicePage = () => {
  const { slug } = useParams<{ slug: string }>();
  const service = servicesData[slug || ""];

  if (!service) {
    return (
      <PageLayout title="Service Not Found" subtitle="The requested service page doesn't exist.">
        <div className="py-20 text-center">
          <Link to="/services" className="inline-flex px-8 py-3 rounded-full gradient-cta text-primary-foreground font-display font-semibold text-sm">
            View All Services
          </Link>
        </div>
      </PageLayout>
    );
  }

  const Icon = service.icon;

  // Check if current service has a specific portfolio to show
  const hasPortfolio = slug === "graphic-designing" || slug === "digital-marketing" || slug === "video-editing";

  return (
    <PageLayout title={service.title} subtitle={service.subtitle}>
      {/* Main Service Content */}
      <section className="py-20">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-16 h-16 rounded-2xl gradient-hero flex items-center justify-center">
              <Icon className="w-8 h-8 text-primary-foreground" />
            </div>
            <h2 className="text-2xl font-bold text-foreground">{service.title}</h2>
          </div>

          {service.description.map((p, i) => (
            <p key={i} className="text-muted-foreground leading-relaxed mb-4">{p}</p>
          ))}

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 my-10">
            {service.stats.map((s) => (
              <div key={s.label} className="text-center bg-card border border-border rounded-2xl p-5">
                <p className="text-2xl font-display font-extrabold text-primary">{s.value}</p>
                <p className="text-xs text-muted-foreground mt-1">{s.label}</p>
              </div>
            ))}
          </div>

          <div className="bg-surface-alt rounded-2xl p-8">
            <h3 className="text-xl font-bold text-foreground mb-6">What's Included</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {service.features.map((f) => (
                <div key={f} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-foreground">{f}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-10 text-center">
            <a
              href="https://wa.me/+923058559591"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex px-8 py-4 rounded-full gradient-cta text-primary-foreground font-display font-semibold text-sm hover:opacity-90 transition-opacity"
            >
              Get A Free Quote on WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* DYNAMIC PORTFOLIO SECTION */}
      {hasPortfolio && (
        <section className="py-12 md:py-20 bg-surface-alt">
          <div className="container mx-auto px-4">
            <div className="text-center mb-10 md:mb-14">
              <span className="inline-block px-5 py-2 rounded-full gradient-cta text-primary-foreground text-xs font-semibold tracking-widest uppercase mb-4">
                Portfolio
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-primary mb-4">
                Our Work
              </h2>
              <p className="text-muted-foreground text-sm md:text-base max-w-2xl mx-auto">
                Take a look at some of our recent projects. We deliver creative solutions that drive real results for our clients.
              </p>
            </div>

            {/* 1. DIGITAL MARKETING PORTFOLIO (Images) */}
            {slug === "digital-marketing" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 md:gap-8 max-w-6xl mx-auto">
                <div className="group relative bg-card border border-border rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300 h-100 md:h-80">
                  <img src={digitalMarketingWork1} alt="Digital Marketing Portfolio 1" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="group relative bg-card border border-border rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300 h-100 md:h-80">
                  <img src={digitalMarketingWork2} alt="Digital Marketing Portfolio 2" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="group relative bg-card border border-border rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300 h-100 md:h-80">
                  <img src={digitalMarketingWork3} alt="Digital Marketing Portfolio 3" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
              </div>
            )}

            {/* 2. GRAPHIC DESIGNING PORTFOLIO (Link) */}
            {slug === "graphic-designing" && (
              <div className="text-center max-w-2xl mx-auto bg-card border border-border rounded-2xl p-8 md:p-12 shadow-sm hover:shadow-md transition-shadow">
                <Palette className="w-12 h-12 text-primary mx-auto mb-6" />
                <h3 className="text-xl md:text-2xl font-bold text-foreground mb-4">Explore Our Design Gallery</h3>
                <p className="text-muted-foreground mb-8">Browse through our extensive collection of brand identities, social media posts, ad creatives, and high-quality visual designs.</p>
                <a 
                  href="https://sites.google.com/view/digital-ibsepro/portfolio?authuser=0" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-full gradient-cta text-primary-foreground font-display font-semibold text-sm hover:opacity-90 transition-opacity"
                >
                  View Graphic Design Portfolio <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            )}

            {/* 3. VIDEO EDITING PORTFOLIO (Link) */}
            {slug === "video-editing" && (
              <div className="text-center max-w-2xl mx-auto bg-card border border-border rounded-2xl p-8 md:p-12 shadow-sm hover:shadow-md transition-shadow">
                <Video className="w-12 h-12 text-primary mx-auto mb-6" />
                <h3 className="text-xl md:text-2xl font-bold text-foreground mb-4">Watch Our Video Showreels</h3>
                <p className="text-muted-foreground mb-8">Check out our high-quality video edits, promotional clips, corporate videos, and motion graphics projects.</p>
                <a 
                  href="https://drive.google.com/drive/folders/1hZMdy5KIZAOIIgX-WWUrhj4rGTBuugg0" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-full gradient-cta text-primary-foreground font-display font-semibold text-sm hover:opacity-90 transition-opacity"
                >
                  View Video Editing Portfolio <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            )}

          </div>
        </section>
      )}

    </PageLayout>
  );
};

export default ServicePage;
